import {useMessages} from "i18n";
import {
	buildMonthGrid,
	DayState,
	monthLabelParts,
	rangeDays,
	shiftMonth,
	type DateRange,
	type DayStateValue,
	type SponsorAvailability,
	SPONSOR_CALENDAR_TESTID,
} from "../sponsorCalendar";
import {
	CalendarFrame,
	Day,
	Grid,
	Legend,
	LegendItem,
	LegendSwatch,
	MonthBar,
	MonthLabel,
	MonthNavButton,
	SelectionBar,
	SelectionHint,
	SelectionText,
	Weekday,
	WeekdayRow,
} from "./SponsorCalendar.styles";

/**
 * The booking calendar (ui#496). A dumb view (epic #26): it renders the grid
 * {@link buildMonthGrid} computes and reports clicks — which day may be clicked, and what clicking it
 * does to the range, are decided in `sponsorCalendar.ts`, which imports no React and is tested alone.
 *
 * Month names and weekday names come from the catalog rather than `toLocaleString`, because the runtime
 * locale here is the app's (hostname-derived), not the browser's: `toLocaleString` would happily emit
 * "September" on a page that is otherwise entirely Romanian.
 */

export interface SponsorCalendarProps {
	availability: SponsorAvailability;
	/** The month on show, `YYYY-MM`. */
	month: string;
	selection: DateRange | null;
	onMonthChange: (month: string) => void;
	onDayClick: (date: string) => void;
}

/** The states a day cell cannot be clicked in. */
const UNCLICKABLE: readonly DayStateValue[] = [DayState.Padding, DayState.Booked, DayState.OutOfWindow];

/** The four states the legend explains — the ones a day INSIDE the bookable window can be in. */
const LEGEND_STATES = [
	{state: DayState.Available, key: "available"},
	{state: DayState.Selected, key: "selected"},
	{state: DayState.Pending, key: "pending"},
	{state: DayState.Booked, key: "booked"},
] as const;

export function SponsorCalendar({
	availability,
	month,
	selection,
	onMonthChange,
	onDayClick,
}: SponsorCalendarProps) {
	const m = useMessages().sponsor.calendar;
	const cells = buildMonthGrid(month, availability, selection);
	const {month: monthIndex, year} = monthLabelParts(month);

	// Navigation is bounded by the window the api published: months with nothing bookable in them are
	// not somewhere to strand a sponsor. Compared as `YYYY-MM` strings, which sort correctly by design.
	const previous = shiftMonth(month, -1);
	const next = shiftMonth(month, 1);
	const days = rangeDays(selection);

	/** The word a screen reader hears for a day's state. */
	const stateWord = (state: DayStateValue): string => {
		switch (state) {
			case DayState.Selected:
				return m.legend.selected;
			case DayState.Booked:
				return m.legend.booked;
			case DayState.Pending:
				return m.legend.pending;
			case DayState.OutOfWindow:
				return m.outOfWindow;
			default:
				return m.legend.available;
		}
	};

	return (
		// Addressable so a spec can measure the FOOTPRINT (ui#572). The grid has no accessible name of its
		// own — its days and its month heading carry the semantics — so there is no role-and-name selector
		// that resolves to the frame itself, which is the box whose size is the thing being asserted.
		<CalendarFrame data-testid={SPONSOR_CALENDAR_TESTID}>
			<MonthBar>
				<MonthNavButton
					aria-label={m.previousMonth}
					disabled={previous < availability.from.slice(0, 7)}
					onClick={() => onMonthChange(previous)}
				>
					‹
				</MonthNavButton>
				<MonthLabel>{m.monthLabel(m.months[monthIndex - 1], year)}</MonthLabel>
				<MonthNavButton
					aria-label={m.nextMonth}
					disabled={next > availability.to.slice(0, 7)}
					onClick={() => onMonthChange(next)}
				>
					›
				</MonthNavButton>
			</MonthBar>

			<WeekdayRow>
				{m.weekdays.map((short, index) => (
					<Weekday key={short}>
						<abbr title={m.weekdaysLong[index]}>{short}</abbr>
					</Weekday>
				))}
			</WeekdayRow>

			<Grid>
				{cells.map((cell, index) => (
					<Day
						// Padding cells have no date, so the position is the only stable identity they have.
						key={cell.date ?? `padding-${index}`}
						$state={cell.state}
						disabled={UNCLICKABLE.includes(cell.state)}
						// A padding cell is spacing, not a date: it is hidden from assistive tech entirely
						// rather than announced as an empty button.
						aria-hidden={cell.date === null || undefined}
						tabIndex={cell.date === null ? -1 : undefined}
						aria-label={cell.date === null ? undefined : m.dayAria(cell.date, stateWord(cell.state))}
						onClick={cell.date === null ? undefined : () => onDayClick(cell.date as string)}
					>
						{cell.date === null ? "" : Number(cell.date.slice(8, 10))}
					</Day>
				))}
			</Grid>

			<Legend>
				{LEGEND_STATES.map(({state, key}) => (
					<LegendItem key={key}>
						<LegendSwatch $state={state} aria-hidden="true"/>
						{m.legend[key]}
					</LegendItem>
				))}
			</Legend>

			<SelectionBar>
				<SelectionText>{selection === null ? m.nothingSelected : m.selection(days)}</SelectionText>
				<SelectionHint>{m.maxDays(availability.maxDays)}</SelectionHint>
			</SelectionBar>
		</CalendarFrame>
	);
}
